import type { CSSProperties, ReactNode } from "react";
import { useLayoutEffect, useMemo, useRef } from "react";
import { recall, remember } from "@/hooks/use-last-known";
import { cn } from "@/lib/utils";
import { indexedArray } from "@/utils/indexed-array";

// Placeholders that take up exactly the space of what they stand in for, so
// nothing moves when the data arrives. Render them inside the element that
// will hold the real content (a title, a cell, a card body) instead of in
// place of it, and the surrounding layout stays the same.

interface SkeletonBoxProps {
  className?: string;
  style?: CSSProperties;
}

/**
 * A block placeholder. Size it with the classes of the element it stands in
 * for, e.g. `h-10 w-full` for an input or `size-8 rounded-full` for an avatar.
 */
export function SkeletonBox({ className, style }: SkeletonBoxProps) {
  return (
    <span
      aria-hidden
      className={cn("skeleton block rounded-md", className)}
      style={style}
    />
  );
}

interface SkeletonTextProps {
  /** Width of a single line, or of the last line when there are several. */
  width?: CSSProperties["width"];
  lines?: number;
  className?: string;
}

/**
 * A text placeholder exactly one line of the surrounding text tall, per line.
 * It takes its height from the inherited font size and line height, so put it
 * where the text will be, e.g. `<CardTitle><SkeletonText /></CardTitle>`.
 */
export function SkeletonText({ width, lines = 1, className }: SkeletonTextProps) {
  if (lines <= 1) {
    return (
      <SkeletonLine
        className={cn("inline-block max-w-full", className)}
        width={width ?? "8rem"}
      />
    );
  }

  return (
    <span aria-hidden className={cn("block", className)}>
      {indexedArray(lines).map((i) => (
        <SkeletonLine
          key={i}
          className="block"
          width={i === lines - 1 ? (width ?? "60%") : "100%"}
        />
      ))}
    </span>
  );
}

function SkeletonLine({
  width,
  className,
}: {
  width: CSSProperties["width"];
  className?: string;
}) {
  // One line tall (1lh). Top-aligned, so a heading whose font lacks the zero
  // width non-joiner (Plus Jakarta Sans) doesn't grow around the fallback
  // font that draws it. The non-joiner gives the height where 1lh isn't
  // supported.
  return (
    <span
      aria-hidden
      className={cn("relative h-[1lh] align-top", className)}
      style={{ width }}
    >
      {"\u200C"}
      <span className="skeleton absolute inset-x-0 top-1/2 h-[0.75em] max-h-full -translate-y-1/2 rounded" />
    </span>
  );
}

/**
 * A label and an input, matching `FormItem` + `FormLabel` + a default `Input`
 * or select.
 */
export function SkeletonField({
  labelWidth = "6rem",
  inputClassName,
  className,
}: {
  labelWidth?: CSSProperties["width"];
  inputClassName?: string;
  className?: string;
}) {
  return (
    <div aria-hidden className={cn("space-y-2", className)}>
      <div className="text-sm font-semibold leading-none">
        <SkeletonText width={labelWidth} />
      </div>
      <SkeletonBox className={cn("h-10 w-full", inputClassName)} />
    </div>
  );
}

interface RememberedWidthTextProps {
  // Names the text across page loads, e.g. `formTitle_${formId}`.
  rememberAs: string;
  loading: boolean;
  // The skeleton's width the first time, before a width is remembered.
  fallbackWidth?: string | number;
  // For the skeleton.
  className?: string;
  children?: ReactNode;
}

/**
 * Text that varies with the data and has something after it, like a title
 * with an edit button, or that may wrap, like a page title beside buttons.
 * While `loading` it is a skeleton with as many lines as the text had the
 * last time this browser showed it, the last as wide as the text's last
 * line, so what follows stays put when the text arrives.
 */
export function RememberedWidthText({
  rememberAs,
  loading,
  fallbackWidth = "14rem",
  className,
  children,
}: RememberedWidthTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const key = `textWidth_${rememberAs}`;
  const linesKey = `textLines_${rememberAs}`;
  const known = useMemo(
    () =>
      loading
        ? { width: recall<number>(key), lines: recall<number>(linesKey) }
        : undefined,
    [loading, key, linesKey],
  );
  useLayoutEffect(() => {
    if (!loading && ref.current) {
      // One rect per line the text wraps onto, and none in a hidden copy
      // (FormBox renders its description twice, for wide and narrow
      // screens), which mustn't overwrite what the visible one measured.
      const lines = ref.current.getClientRects();
      const lastLine = lines[lines.length - 1];
      if (!lastLine) {
        return;
      }
      remember(key, lastLine.width);
      // Most texts fit on one line; only store a count that isn't 1, or
      // that replaces one that wasn't.
      if (lines.length !== 1 || recall<number>(linesKey) !== undefined) {
        remember(linesKey, lines.length);
      }
    }
  }, [loading, children, key, linesKey]);

  if (loading) {
    return (
      <SkeletonText
        lines={known?.lines ?? 1}
        width={known?.width ?? fallbackWidth}
        className={className}
      />
    );
  }
  return <span ref={ref}>{children}</span>;
}

/**
 * A placeholder exactly the size of `children`, which render invisibly under
 * it. For content whose size is known before the data is, like a button
 * with one of a few fixed labels.
 */
export function SkeletonSizedBy({ children }: { children: ReactNode }) {
  return (
    <span aria-hidden className="relative inline-flex">
      <span className="invisible inline-flex">{children}</span>
      <span className="skeleton absolute inset-0 rounded-md" />
    </span>
  );
}
